/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Agents overview", description = "Ask questions about your data, analyze campaign performance, build audiences, and automate reporting in natural language. Powered by your data warehouse and connected marketing channels.", lastUpdatedDate = new Date(1792368000000), keywords = "llm, claude, chatgpt, ai assistant, large language model";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Agents overview",
        description: "Ask questions about your data, analyze campaign performance, build audiences, and automate reporting in natural language. Powered by your data warehouse and connected marketing channels.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Core features",
          "depth": 0,
          "id": "core-features"
        }, {
          "value": "Chats",
          "depth": 1,
          "id": "chats"
        }, {
          "value": "Reports",
          "depth": 1,
          "id": "reports"
        }, {
          "value": "Custom agents",
          "depth": 1,
          "id": "custom-agents"
        }, {
          "value": "Settings",
          "depth": 1,
          "id": "settings"
        }, {
          "value": "Context Hub",
          "depth": 1,
          "id": "context-hub"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "For data teams and platform admins",
          "depth": 1,
          "id": "for-data-teams-and-platform-admins"
        }, {
          "value": "For marketers",
          "depth": 1,
          "id": "for-marketers"
        }, {
          "value": "When to use Agents",
          "depth": 0,
          "id": "when-to-use-agents"
        }, {
          "value": "Use Agents with other Hightouch products",
          "depth": 0,
          "id": "use-agents-with-other-hightouch-products"
        }, {
          "value": "Data governance",
          "depth": 0,
          "id": "data-governance"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A ", _jsx(_components.a, {
                href: "/sources/overview",
                children: "data warehouse"
              }), " connected as a source"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Ask questions about your customer data and marketing performance in natural language. Agents return answers, draft reports, and help build audiences using your warehouse data and connected marketing channels."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Agents lets you explore customer data, analyze campaign performance, and automate reporting in natural language. Describe what you need and the agent returns answers based on your warehouse data and connected marketing tools."
      }), "\n", _jsx(_components.p, {
        children: "Agents connect to your data warehouse and marketing channels like Meta, Google Ads, TikTok, Braze, and Iterable. They query across your full customer and campaign data, not just what's available in a single tool."
      }), "\n", _jsx(_components.p, {
        children: "Agents are designed for marketing workflows like campaign analysis, audience research, and reporting. Agents understand concepts like creative fatigue, audience segmentation, and experiment design, so they can return actionable answers rather than raw data."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/overview/overview.png",
          alt: "Agents chat interface"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "core-features",
        children: "Core features"
      }), "\n", _jsxs(_components.p, {
        children: ["Agents has four main areas, each accessible from the left navigation inside the ", _jsx(_components.strong, {
          children: "Agents"
        }), " tab."]
      }), "\n", _jsx(_components.h3, {
        id: "chats",
        children: "Chats"
      }), "\n", _jsx(_components.p, {
        children: "The primary interface for asking questions and exploring data. You type a question in natural language, and the agent queries your warehouse, analyzes results, and returns answers with charts, tables, and recommendations."
      }), "\n", _jsx(_components.p, {
        children: "Conversations are persistent. You can follow up, refine, and dig deeper without starting over. The agent also suggests questions based on your data to help you get started."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/agents/chats",
          children: "Chats"
        }), " for more detail."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/chats/chat-overview-nav.png",
          alt: "Chats interface"
        })
      }), "\n", _jsx(_components.h3, {
        id: "reports",
        children: "Reports"
      }), "\n", _jsx(_components.p, {
        children: "Saved prompts that run on a schedule and deliver results automatically. You can save any one-off chat answer as a recurring report delivered via email or Slack."
      }), "\n", _jsx(_components.p, {
        children: "Reports are useful for weekly performance summaries, daily campaign dashboards, and automated monitoring that would otherwise require data team support."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/agents/reports",
          children: "Reports"
        }), " for more detail."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/reports/reports-list.png",
          alt: "Reports list"
        })
      }), "\n", _jsx(_components.h3, {
        id: "custom-agents",
        children: "Custom agents"
      }), "\n", _jsx(_components.p, {
        children: "Specialized agents scoped to specific teams, metrics, or use cases. A custom agent has a defined task (for example, \"Paid Media Analyst\" or \"Lifecycle Performance Analyst\"), and tone and guardrails you configure."
      }), "\n", _jsx(_components.p, {
        children: "End users select a custom agent from the chat interface when they want analysis scoped to a particular domain."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/agents/custom-agents",
          children: "Custom agents"
        }), " for more detail."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/custom-agents/custom-agents-list.png",
          alt: "Custom agents list"
        })
      }), "\n", _jsx(_components.h3, {
        id: "settings",
        children: "Settings"
      }), "\n", _jsx(_components.p, {
        children: "Configure workspace-level settings like your default parent model, reviewers, and inference providers."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/agents/settings",
          children: "Settings"
        }), " for more detail."]
      }), "\n", _jsx(_components.h3, {
        id: "context-hub",
        children: "Context Hub"
      }), "\n", _jsx(_components.p, {
        children: "Context Hub is a separate top-level section where you manage the knowledge, brand assets, and marketing connections that agents use. This includes files, definitions, brand profiles, guardrails, and channel connections."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/context-hub/overview",
          children: "Context Hub"
        }), " for more detail."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsx(_components.p, {
        children: "Agents requires setup from data teams before marketers can use it. This setup is typically done once per workspace."
      }), "\n", _jsx(_components.h3, {
        id: "for-data-teams-and-platform-admins",
        children: "For data teams and platform admins"
      }), "\n", _jsx(_components.p, {
        children: "Complete these steps in order. Marketers cannot use Agents until a warehouse is connected and permissions are configured."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Step"
            }), _jsx(_components.th, {
              children: "What to do"
            }), _jsx(_components.th, {
              children: "Why"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/sources/overview",
                children: "Connect a data warehouse"
              })
            }), _jsx(_components.td, {
              children: "Agents queries warehouse data directly, so it needs at least one connected source."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "2"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "Set up your schema"
              }), " or ", _jsx(_components.a, {
                href: "/customer-studio/general-models",
                children: "add general models"
              })]
            }), _jsx(_components.td, {
              children: "Agents requires at least one parent model or general model to query. Without these, the agent has no tables to work with."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/business-context#add-business-context",
                children: "Add organizational context"
              })
            }), _jsx(_components.td, {
              children: "Provide metric definitions, business rules, and background the agent should know. You can also upload files like planning documents or report templates."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "4"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/context-hub/connections",
                children: "Connect marketing channels"
              }), " (optional)"]
            }), _jsx(_components.td, {
              children: "Add ad platforms and ESPs (Meta, Google Ads, TikTok, Braze, Iterable, and others) so the agent can include campaign performance data and creative analysis in its answers."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "5"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/context-hub/brands",
                children: "Configure brand guidelines"
              }), " (optional)"]
            }), _jsx(_components.td, {
              children: "Add brand profiles, font preferences, and email best practices for creative content generation in Ad Studio and Lifecycle Studio."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "6"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#data-governance",
                children: "Enable Agents for your group"
              })
            }), _jsxs(_components.td, {
              children: ["Grant the ", _jsx(_components.strong, {
                children: "Access Agents"
              }), " permission to each group that should have access. See ", _jsx(_components.a, {
                href: "/workspace-management/roles#agents",
                children: "Roles"
              }), " for details."]
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you already have a Customer Studio schema or ", _jsx(_components.a, {
            href: "/customer-studio/general-models",
            children: "general models"
          }), " configured, Agents can query that data immediately. Connecting marketing channels adds richer campaign and creative performance context but isn't required to start chatting."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "for-marketers",
        children: "For marketers"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your data team has configured the workspace, start by ", _jsx(_components.a, {
          href: "/agents/chats",
          children: "opening a new chat"
        }), ". Describe what you want to know, like performance metrics, audience breakdowns, comparisons, or anomaly detection. The agent returns answers with charts, tables, and recommendations."]
      }), "\n", _jsxs(_components.p, {
        children: ["From there, ", _jsx(_components.a, {
          href: "/agents/custom-agents",
          children: "save reusable prompts as custom agents"
        }), " to standardize analysis for your team, and ", _jsx(_components.a, {
          href: "/agents/reports",
          children: "create scheduled reports"
        }), " to automate recurring analysis via email or Slack. After any analysis, you can ask the agent to ", _jsx(_components.a, {
          href: "/agents/chats#go-from-insight-to-action",
          children: "build an audience"
        }), " and save it to Customer Studio for activation."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Customer Studio also includes an ", _jsx(_components.a, {
            href: "/customer-studio/agents",
            children: "Agent in the audience builder"
          }), " for creating, refining, and understanding audiences using natural language. Use Agents when you're starting from a question and want to explore your data first. Use the Agent in the audience builder when you already know the audience you need and want help defining it. See ", _jsx(_components.a, {
            href: "/agents/compare-agents",
            children: "Which agent should I use?"
          }), " for a detailed breakdown."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "when-to-use-agents",
        children: "When to use Agents"
      }), "\n", _jsx(_components.p, {
        children: "Agents is the right tool for ad hoc exploration: open-ended questions, one-off comparisons, and analyses that don't fit a pre-built view. For structured dashboards, use Intelligence instead."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Scenario"
            }), _jsx(_components.th, {
              children: "Recommended approach"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Explore customer data or campaign performance with natural-language questions"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agents"
              }), " ", _jsx(_components.a, {
                href: "/agents/chats",
                children: "Chats"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Automate recurring analysis and deliver via email or Slack"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agents"
              }), " ", _jsx(_components.a, {
                href: "/agents/reports",
                children: "Reports"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Standardize analysis across your team with saved prompts"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agents"
              }), " ", _jsx(_components.a, {
                href: "/agents/custom-agents",
                children: "Custom agents"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Build audiences from analysis results"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agents"
              }), " then ", _jsx(_components.a, {
                href: "/customer-studio/overview",
                children: "Customer Studio"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Track KPIs with structured dashboards and pre-built charts"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/campaign-intelligence/overview",
                children: "Intelligence"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Run holdout tests and measure incremental lift"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/campaign-intelligence/overview",
                children: "Intelligence"
              }), " ", _jsx(_components.a, {
                href: "/campaign-intelligence/experiments",
                children: "Experiments"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Automate which message, channel, and timing each user receives"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/overview",
                children: "AI Decisioning"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're not sure where to start, try asking the agent what you should be looking at: \"What are the top 5 things I should look at this week for my paid media campaigns?\" or \"What questions should I be asking about my email performance?\" For tips on getting better results from your prompts, see ", _jsx(_components.a, {
          href: "/agents/chats#tips-for-better-results",
          children: "Tips for better results"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-agents-with-other-hightouch-products",
        children: "Use Agents with other Hightouch products"
      }), "\n", _jsx(_components.p, {
        children: "Agents answers questions using your warehouse data and connected channels. You can send its outputs — audiences, reports, and briefs — to other Hightouch products to act on."
      }), "\n", _jsx(_components.h4, {
        id: "data-and-context-agents-reads",
        children: "Data and context Agents reads"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Warehouse data from your ", _jsx(_components.a, {
              href: "/sources/overview",
              children: "connected source"
            })]
          }), " — customer records, transactions, product catalogs, and any other tables you expose through your Customer Studio schema and general models."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connected marketing channels"
          }), " — ad platforms and ESPs connected in Context Hub's ", _jsx(_components.a, {
            href: "/context-hub/connections",
            children: "Connected data"
          }), ", which provide campaign performance metrics and ad creatives directly."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization context"
          }), " — brand guidelines, marketing best practices, and organizational knowledge configured in ", _jsx(_components.a, {
            href: "/context-hub/overview",
            children: "Context Hub"
          }), ", including ", _jsx(_components.a, {
            href: "/context-hub/business-context#add-business-context",
            children: "files"
          }), ", ", _jsx(_components.a, {
            href: "/context-hub/business-context#review-and-approve-definitions",
            children: "definitions"
          }), ", and ", _jsx(_components.a, {
            href: "/context-hub/brands",
            children: "brand profiles"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "audiences-reports-and-briefs-you-create",
        children: "Audiences, reports, and briefs you create"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Audiences to ", _jsx(_components.a, {
              href: "/customer-studio/overview",
              children: "Customer Studio"
            })]
          }), " — ask an agent to build an audience from your analysis, then review and save it to Customer Studio for activation."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Reports to email or Slack"
          }), " — save any one-off analysis as a ", _jsx(_components.a, {
            href: "/agents/reports",
            children: "scheduled report"
          }), " delivered automatically to your team."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Briefs for Ad Studio or Lifecycle Studio"
          }), " — use agent outputs as a starting point for creative development in Ad Studio or campaign planning in Lifecycle Studio."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "data-governance",
        children: "Data governance"
      }), "\n", _jsx(_components.p, {
        children: "Agents respects the same governance framework used across Hightouch. Every agent query is scoped to the data the current user is authorized to access — agents never bypass per-user access controls, and they never create or change anything without explicit user approval."
      }), "\n", _jsx(_components.p, {
        children: "Four layers control what an agent can see and do:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Layer"
            }), _jsx(_components.th, {
              children: "What it controls"
            }), _jsx(_components.th, {
              children: "Where to configure"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Feature access"
              })
            }), _jsx(_components.td, {
              children: "Whether a user can open Agents at all, and whether they can modify settings"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Access Agents"
              }), " and ", _jsx(_components.strong, {
                children: "Configure Agents"
              }), " grants in ", _jsx(_components.a, {
                href: "/workspace-management/roles#agents",
                children: "Roles"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Table access"
              })
            }), _jsxs(_components.td, {
              children: ["Which warehouse tables agents can query — via ", _jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "Customer Studio schema"
              }), " models and ", _jsx(_components.a, {
                href: "/customer-studio/general-models",
                children: "general models"
              })]
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Customer Studio"
              }), ", then ", _jsx(_components.strong, {
                children: "Schema"
              }), " (parent/related/event models and general models drawer)"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Column access"
              })
            }), _jsx(_components.td, {
              children: "Which columns are visible to agents — privacy levels control whether a column is blocked, redacted, or fully available"
            }), _jsxs(_components.td, {
              children: ["Per-model column settings in the ", _jsx(_components.a, {
                href: "/customer-studio/general-models#column-privacy",
                children: "general models"
              }), " or schema model detail view"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Row-level access"
              })
            }), _jsx(_components.td, {
              children: "Which rows agents can return, based on filter rules assigned to user groups"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/customer-studio/subsets",
                children: "Row-level access"
              }), " rule groups in ", _jsx(_components.strong, {
                children: "Customer Studio"
              }), ", then ", _jsx(_components.strong, {
                children: "Governance"
              })]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Model-level query permissions are managed in the ", _jsx(_components.strong, {
          children: "Models"
        }), " and ", _jsx(_components.strong, {
          children: "Customer Studio"
        }), " tabs of the ", _jsx(_components.a, {
          href: "/workspace-management/roles#custom-role-grant-reference",
          children: "custom role builder"
        }), ". For general models, the relevant grant is ", _jsx(_components.strong, {
          children: "View model data"
        }), ". Creating and managing general models requires the ", _jsx(_components.strong, {
          children: "Configure schema"
        }), " grant on the underlying source."]
      }), "\n", _jsx(_components.p, {
        children: "Agents never create or change anything without approval. During chats, an agent may draft audiences or reports, but users must review and explicitly save those outputs."
      })]
    });
  }
}
export default MDXContent;
